import { getStoreyElevation } from '@shared/helpers/design/storey.helpers';
import { getSolidShapes } from '@shared/helpers/design/cutout.helpers';
import type { Storey } from '@shared/models/design/storey.model';
import type { Point2D, Polygon2D } from '@shared/models/geometry.model';
import type {
  PointPayload,
  ShapeTranslatePayload,
} from '../models/map-observables.models';
import { rotatePolygonAroundCenter } from '../helpers/building-rotation.helpers';

export type PolygonDragOrigin = {
  points: readonly Point2D[];
  elevation: number;
};

/** Resolve the original polygon ring retained beneath an active geometry drag. */
export function resolvePolygonDragOrigin(input: {
  storeys: Storey[];
  pointMove?: PointPayload;
  translation?: ShapeTranslatePayload;
}): PolygonDragOrigin | undefined {
  const { pointMove, storeys, translation } = input;
  if (
    pointMove &&
    !pointMove.isDraft &&
    pointMove.shape.points.length >= 2 &&
    pointMove.elevation !== undefined
  ) {
    return {
      points: pointMove.shape.points,
      elevation: pointMove.elevation,
    };
  }

  if (!translation) return undefined;
  const groundOrigin = translation.groundLevelOriginOutline
    ? resolveGroundLevelTranslationOrigin(storeys, translation)
    : undefined;
  const storey =
    groundOrigin?.storey ??
    storeys.find((candidate) => candidate.id === translation.baseStoreyId);
  const shape =
    groundOrigin?.shape ??
    storey?.shapes.find((candidate) => candidate.id === translation.shapeId);
  if (!storey || !shape?.points.length) return undefined;

  return {
    points: shape.points,
    elevation: getStoreyElevation(storeys, storey),
  };
}

const GROUND_ELEVATION_EPSILON = 1e-6;

/** Pick the translated solid whose floor is at ground, or nearest below it. */
const resolveGroundLevelTranslationOrigin = (
  storeys: Storey[],
  translation: ShapeTranslatePayload,
): { storey: Storey; shape: Storey['shapes'][number] } | undefined => {
  const targetedShapeIds = new globalThis.Map(
    translation.targets.map(({ storeyId, shapeIds }) => [
      storeyId,
      new Set(shapeIds),
    ]),
  );
  const candidates = storeys.flatMap((storey) => {
    const targetIds = targetedShapeIds.get(storey.id);
    if (!targetIds?.size) return [];
    const shape = getSolidShapes(storey).find((candidate) =>
      targetIds.has(candidate.id),
    );
    return shape
      ? [{ storey, shape, elevation: getStoreyElevation(storeys, storey) }]
      : [];
  });
  if (!candidates.length) return undefined;

  const atGround = candidates.find(
    ({ elevation }) => Math.abs(elevation) <= GROUND_ELEVATION_EPSILON,
  );
  const belowGround = candidates
    .filter(({ elevation }) => elevation < -GROUND_ELEVATION_EPSILON)
    .sort((a, b) => b.elevation - a.elevation)[0];
  const fallback = candidates.sort((a, b) => a.elevation - b.elevation)[0];
  const resolved = atGround ?? belowGround ?? fallback;
  return resolved
    ? { storey: resolved.storey, shape: resolved.shape }
    : undefined;
};

/**
 * Apply translation previews emitted by {@link ShapeTranslatePayload} to the storey list.
 * This mirrors the logic in MapRenderer so overlays stay aligned during drags.
 */
export function applyShapeTranslationPreview(
  storeys: Storey[],
  payload: ShapeTranslatePayload,
): Storey[] {
  const [dx, dy] = payload.delta;
  const rotation = payload.rotation;
  if (!dx && !dy && !rotation?.radians) return storeys;

  const byStorey = new globalThis.Map<string, Set<string>>();
  payload.targets.forEach(({ storeyId, shapeIds }) => {
    if (!storeyId || !shapeIds?.length) return;
    const current = byStorey.get(storeyId) ?? new Set<string>();
    shapeIds.forEach((shapeId) => current.add(shapeId));
    byStorey.set(storeyId, current);
  });
  if (!byStorey.size) return storeys;

  let mutated = false;
  const next = storeys.map((storey) => {
    const targetSet = byStorey.get(storey.id);
    if (!targetSet?.size) return storey;

    let storeyMutated = false;
    const shapes = storey.shapes.map((shape) => {
      if (!shape?.points?.length) return shape;
      if (!targetSet.has(shape.id)) return shape;

      storeyMutated = true;
      // Translate in metric space so the overlay stays aligned during drag previews.
      const translated = translatePolygon2D(shape.points, [dx, dy]);
      const transformed = rotation?.radians
        ? rotatePolygonAroundCenter(
            translated,
            rotation.center,
            rotation.radians,
          )
        : translated;
      return { ...shape, points: transformed };
    });

    if (!storeyMutated) return storey;
    mutated = true;
    return {
      ...storey,
      shapes: payload.duplicateBuilding
        ? [
            ...storey.shapes.map((shape) => ({
              ...shape,
              id: `duplicate-origin:${shape.id}`,
            })),
            ...shapes,
          ]
        : shapes,
    };
  });

  return mutated ? next : storeys;
}

function translatePolygon2D(points: Polygon2D, delta: Point2D): Polygon2D {
  const [dx, dy] = delta;
  const [p0, p1, p2, ...rest] = points;

  // Preserve the non-empty tuple shape that {@link Polygon2D} encodes.
  const t0: Point2D = [p0[0] + dx, p0[1] + dy];
  const t1: Point2D = [p1[0] + dx, p1[1] + dy];
  const t2: Point2D = [p2[0] + dx, p2[1] + dy];
  const translatedRest = rest.map(([x, y]) => [x + dx, y + dy] as Point2D);

  return [t0, t1, t2, ...translatedRest];
}
